import React, { useEffect, useMemo, useState } from "react";
import { Drawer, Skeleton, Tabs } from "antd";
import Button from "@dtci/spellbook/Button";
import { usePPTState, usePPTActions } from "tap/store/ppt/usePPT";
import { PPTDiscountDetails } from "../PPTDiscountDetails";
import { PPTDiscountIssuesList } from "../PPTDiscountIssuesList";
import { PPTDiscountAuditTimeline } from "../PPTDiscountDetails/PPTDiscountAuditTimeline";
import { PPTDiscountClientPanel } from "../PPTDiscountDetailsGrid/PPTDiscountClientPanel";
import {
  updateDiscountActiveStatus,
  fetchPromotion,
  fetchPromotionDiscounts,
} from "ppt/api";
import { usePPTToasts } from "../../hooks/usePPTToasts";
import styles from "./pptDiscountDrawer.module.scss";

const TAB_ISSUES = "issues";
const TAB_DETAILS = "details";
const TAB_CLIENTS = "clients";
const TAB_AUDIT = "audit";

export const PPTDiscountDrawer = () => {
  const {
    discountDetailsDrawerOpen,
    selectedDiscount,
    selectedDiscountId,
    discountDetailsLoading,
    promotionDetail,
    discountIssues,
  } = usePPTState();
  const {
    closeDiscountDetailsDrawer,
    updatePromotionDetail,
    updateDiscountDetailsData,
    updateDiscountActiveStatusInStore,
  } = usePPTActions();
  const { showErrorToast } = usePPTToasts();
  const [isToggling, setIsToggling] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_DETAILS);

  // Reset to the Issues tab whenever a different discount is opened, so the
  // operator lands on triage by default for each new drill-in.
  useEffect(() => {
    if (selectedDiscountId) setActiveTab(TAB_DETAILS);
  }, [selectedDiscountId]);

  const handleClose = () => {
    closeDiscountDetailsDrawer();
  };

  const handleToggleActive = async () => {
    if (!selectedDiscount?.id || isToggling) return;

    setIsToggling(true);
    try {
      const newActiveStatus = !selectedDiscount.active;
      const updatedDiscount = await updateDiscountActiveStatus(
        selectedDiscount.id,
        newActiveStatus,
      );
      updateDiscountActiveStatusInStore(updatedDiscount);
    } catch (error) {
      console.error("Failed to update discount status:", error);
      showErrorToast("Failed to update discount status. Please try again.");
      setIsToggling(false);
      return;
    }

    try {
      if (promotionDetail?.id) {
        const [{ data: promotionData }, discountsData] = await Promise.all([
          fetchPromotion(promotionDetail.id),
          fetchPromotionDiscounts(promotionDetail.id),
        ]);
        updatePromotionDetail(promotionData);
        updateDiscountDetailsData(discountsData);
      }
    } catch (error) {
      console.warn(
        "Discount toggle succeeded; failed to refresh promotion view:",
        error,
      );
    } finally {
      setIsToggling(false);
    }
  };

  const drawerTitle = selectedDiscount?.title || "Discount Details";
  const issueCount = discountIssues?.issueCount ?? 0;
  const auditCount = selectedDiscount?.auditEvents?.length ?? 0;
  const clientCount = selectedDiscount?.children?.length ?? 0;

  const tabItems = useMemo(
    () => [
      {
        key: TAB_DETAILS,
        label: "Details",
        children: <PPTDiscountDetails />,
      },
      {
        key: TAB_ISSUES,
        label: issueCount > 0 ? `Issues (${issueCount})` : "Issues",
        children: <PPTDiscountIssuesList />,
      },
      {
        key: TAB_CLIENTS,
        label:
          clientCount > 0
            ? `Client Discounts (${clientCount})`
            : "Client Discounts",
        children: (
          <div className={styles.clientsWrap}>
            <PPTDiscountClientPanel
              data={selectedDiscount}
              showOpenAction={false}
            />
          </div>
        ),
      },
      {
        key: TAB_AUDIT,
        label: auditCount > 0 ? `Audit Log (${auditCount})` : "Audit Log",
        children: (
          <div className={styles.auditWrap}>
            <PPTDiscountAuditTimeline events={selectedDiscount?.auditEvents} />
          </div>
        ),
      },
    ],
    [issueCount, auditCount, clientCount, selectedDiscount],
  );

  return (
    <Drawer
      className="discount-details-drawer"
      title={drawerTitle}
      width={750}
      placement="right"
      open={discountDetailsDrawerOpen}
      onClose={handleClose}
      bodyStyle={{ padding: 0, backgroundColor: "#f5f5f5" }}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            iconType="fa-solid fa-xmark"
            labelText="close"
            styleType="link"
            onClick={handleClose}
          >
            Close
          </Button>
          <Button
            styleType="standard"
            colorType={selectedDiscount?.active ? "error" : "primary"}
            onClick={handleToggleActive}
            isFetching={isToggling}
          >
            {selectedDiscount?.active
              ? "Deactivate Discount"
              : "Activate Discount"}
          </Button>
        </div>
      }
    >
      <Skeleton
        active
        paragraph={{ rows: 10 }}
        loading={discountDetailsLoading}
        style={{ padding: 24 }}
      >
        <Tabs
          className={styles.tabs}
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          destroyInactiveTabPane={false}
        />
      </Skeleton>
    </Drawer>
  );
};
